import { useState, useCallback } from 'react';
import { useTranslation } from '../../i18n';
import { useCalendarDataContext } from '../../contexts/CalendarDataContext';
import useDialog from '../../hooks/useDialog';
import { exportToJSON, exportToICS, importFromJSON } from '../../utils/exportUtils';

/**
 * Custom hook for managing modal state and handlers in the Calendar component.
 * Handles project management, settings, holidays, and import/export modals.
 */
function useCalendarModals() {
  const { t } = useTranslation();
  const { confirm: confirmDialog, alert: alertDialog } = useDialog();
  const {
    projects,
    events,
    milestones,
    addProject,
    updateProject,
    deleteProject,
    deleteEvent,
    deleteMilestone,
    updateEvent,
    updateMilestone,
    importData,
    data
  } = useCalendarDataContext();

  // Modal visibility states
  const [showProjectForm, setShowProjectForm] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showHolidayModal, setShowHolidayModal] = useState(false);
  const [showICalExportModal, setShowICalExportModal] = useState(false);
  const [showCalendarManager, setShowCalendarManager] = useState(false);
  const [showICalSettings, setShowICalSettings] = useState(false);
  const [showSharingPanel, setShowSharingPanel] = useState(false);

  // Project management handlers
  const handleAddProject = useCallback(() => {
    setEditingProject(null);
    setShowProjectForm(true);
  }, []);

  const handleEditProject = useCallback((project) => {
    setEditingProject(project);
    setShowProjectForm(true);
  }, []);

  const handleSaveProject = useCallback(async (name, color) => {
    if (editingProject) {
      if (editingProject.isIncluded) {
        // For included/shared projects, save local customizations
        const { shares } = await import('../../api/index.js');
        await shares.updateIncluded(editingProject.includedId, {
          customName: name !== editingProject.originalName ? name : null,
          customColor: color !== editingProject.originalColor ? color : null,
        });
        // Update local state with the customized values
        await updateProject(editingProject.id, { name, color }, true);
      } else {
        await updateProject(editingProject.id, { name, color });
      }
    } else {
      await addProject(name, color);
    }
    setShowProjectForm(false);
    setEditingProject(null);
  }, [editingProject, addProject, updateProject]);

  const handleCancelProject = useCallback(() => {
    setShowProjectForm(false);
    setEditingProject(null);
  }, []);

  const handleDeleteProject = useCallback(async (targetProjectId) => {
    if (!editingProject) return;
    
    if (projects.length <= 1) {
      await alertDialog({
        title: t('dialog.deleteProject'),
        message: t('dialog.deleteProjectLastWarning'),
        variant: 'error'
      });
      return;
    }
    
    let confirmMessage;
    if (targetProjectId) {
      const targetProject = projects.find(p => p.id === targetProjectId);
      confirmMessage = t('dialog.deleteProjectConfirmMerge', { 
        name: editingProject.name,
        targetName: targetProject?.name || ''
      });
    } else {
      confirmMessage = t('dialog.deleteProjectConfirmDelete', { 
        name: editingProject.name 
      });
    }
    
    const confirmed = await confirmDialog({
      title: t('dialog.deleteProject'),
      message: confirmMessage,
      confirmLabel: t('common.delete'),
      variant: 'danger'
    });
    
    if (confirmed) {
      if (targetProjectId === null) {
        await Promise.all(
          events.filter(e => e.projectId === editingProject.id).map(e => deleteEvent(e.id))
        );
        await Promise.all(
          milestones.filter(m => m.projectId === editingProject.id).map(m => deleteMilestone(m.id))
        );
      } else {
        await Promise.all(
          events.filter(e => e.projectId === editingProject.id).map(e => 
            updateEvent(e.id, { projectId: targetProjectId })
          )
        );
        await Promise.all(
          milestones.filter(m => m.projectId === editingProject.id).map(m =>
            updateMilestone(m.id, { projectId: targetProjectId })
          )
        );
      }
      
      await deleteProject(editingProject.id);
      setShowProjectForm(false);
      setEditingProject(null);
    }
  }, [editingProject, projects, events, milestones, deleteProject, deleteEvent, deleteMilestone, updateEvent, updateMilestone, confirmDialog, alertDialog, t]);

  // Export/Import handlers
  const handleExport = useCallback(async () => {
    try {
      exportToJSON(data);
    } catch (error) {
      await alertDialog({
        title: t('dialog.exportFailed'),
        message: t('dialog.exportFailedMessage', { error: error.message })
      });
    }
  }, [data, alertDialog, t]);

  const handleImport = useCallback(async () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      
      try {
        const importedData = await importFromJSON(file);
        const confirmed = await confirmDialog({
          title: t('dialog.importTitle'),
          message: t('dialog.importConfirm'),
          confirmLabel: t('common.confirm')
        });
        
        if (confirmed) {
          await importData(importedData);
          await alertDialog({
            title: t('dialog.importSuccess'),
            message: t('dialog.importSuccessMessage')
          });
        }
      } catch (error) {
        await alertDialog({
          title: t('dialog.importFailed'),
          message: t('dialog.importFailedMessage', { error: error.message })
        });
      }
    };
    input.click();
  }, [importData, confirmDialog, alertDialog, t]);

  // Settings handlers
  const handleOpenSettings = useCallback(() => {
    setShowSettingsModal(true);
  }, []);

  const handleCloseSettings = useCallback(() => {
    setShowSettingsModal(false);
  }, []);

  const handleManageHolidays = useCallback(() => {
    setShowSettingsModal(false);
    setShowHolidayModal(true);
  }, []);

  const handleOpenICalExport = useCallback(() => {
    setShowSettingsModal(false);
    setShowICalExportModal(true);
  }, []);

  const handleCloseICalExport = useCallback(() => {
    setShowICalExportModal(false);
  }, []);

  const handleICalExportConfirm = useCallback(async (selectedProjectIds) => {
    try {
      // Filter events and milestones by selected projects
      const selectedProjectSet = new Set(selectedProjectIds);
      const filteredEvents = events.filter(event => selectedProjectSet.has(event.projectId));
      const filteredMilestones = milestones.filter(milestone => selectedProjectSet.has(milestone.projectId));
      
      // Export to iCalendar - use data.projects to include all projects (including Holidays)
      exportToICS(filteredEvents, filteredMilestones, data.projects);
      
      // Close modal
      setShowICalExportModal(false);
    } catch (error) {
      await alertDialog({
        title: t('dialog.exportFailed'),
        message: t('dialog.exportFailedMessage', { error: error.message })
      });
      // Still close the modal even if export fails
      setShowICalExportModal(false);
    }
  }, [events, milestones, data.projects, alertDialog, t]);

  const handleCloseHolidayModal = useCallback(() => {
    setShowHolidayModal(false);
  }, []);

  // Calendar manager handlers
  const handleOpenCalendarManager = useCallback(() => {
    setShowCalendarManager(true);
  }, []);

  const handleCloseCalendarManager = useCallback(() => {
    setShowCalendarManager(false);
  }, []);

  // iCal settings handlers
  const handleOpenICalSettings = useCallback(() => {
    setShowSettingsModal(false);
    setShowICalSettings(true);
  }, []);

  const handleCloseICalSettings = useCallback(() => {
    setShowICalSettings(false);
  }, []);

  return {
    // State
    showProjectForm,
    editingProject,
    showSettingsModal,
    showHolidayModal,
    showICalExportModal,
    showCalendarManager,
    showICalSettings,
    // Project handlers
    handleAddProject,
    handleEditProject,
    handleSaveProject,
    handleCancelProject,
    handleDeleteProject,
    // Export/Import handlers
    handleExport,
    handleImport,
    // Settings handlers
    handleOpenSettings,
    handleCloseSettings,
    handleManageHolidays,
    // iCal export handlers
    handleOpenICalExport,
    handleCloseICalExport,
    handleICalExportConfirm,
    // Holiday handlers
    handleCloseHolidayModal,
    // Calendar manager handlers
    handleOpenCalendarManager,
    handleCloseCalendarManager,
    // iCal settings handlers
    handleOpenICalSettings,
    handleCloseICalSettings,
    // Sharing panel handlers
    showSharingPanel,
    handleOpenSharingPanel: useCallback(() => setShowSharingPanel(true), []),
    handleCloseSharingPanel: useCallback(() => setShowSharingPanel(false), [])
  };
}

export default useCalendarModals;
